/**
 * Sessiya holati. Token cookie'da bo'lgani uchun frontend faqat «kim kirgan»
 * ma'lumotini saqlaydi — sahifa yangilanganda `GET /auth/me/` bilan tiklanadi.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { authApi } from "@/shared/api/endpoints";
import { ApiError } from "@/shared/api/client";
import type { User } from "@/shared/api/types";

type Status = "checking" | "authenticated" | "anonymous";

interface AuthValue {
  user: User | null;
  status: Status;
  /** Backend ulanmagan konturda dashboard demo ma'lumot bilan ko'rsatiladi. */
  offline: boolean;
  /** Sessiya sahifa ochilganda cookie orqali tiklandimi (yangi login emas). */
  restored: boolean;
  setSession: (user: User) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Faolsizlik sessiyasi: 15 daqiqa harakatsiz qolsa chiqariladi. */
export const IDLE_LIMIT_MS = 15 * 60 * 1000;
const ACTIVITY_KEY = "sf_last_activity";
/** Kirish sahifasi shu belgi bo'yicha «sessiya tugadi» xabarini ko'rsatadi. */
export const IDLE_FLAG_KEY = "sf_idle_logout";
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart", "wheel"];

function readActivity(): number {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Tashqi foydalanuvchi (ijarachi, xaridor, fuqaro) — u xodimlar kabinetiga
 * emas, mijoz kabinetiga tushadi. Xizmat hisoblari (integratsiya) interfeysga
 * umuman kirmaydi, shuning uchun ular xodim sifatida qaraladi.
 */
export function isClientUser(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.subject_type === "individual" || user.subject_type === "legal_entity") return true;
  /* Xaridor/ijarachi roli (tashqi rol) xodim yozuvida ham bo'lishi mumkin: faqat
     shunday rollari bor foydalanuvchi mijoz kabinetiga tushadi. Sotuvchi — ichki rol. */
  const roles = user.role_codes ?? [];
  return (
    roles.length > 0 &&
    roles.every((code) => code === "buyer" || code === "lessee") &&
    Boolean(user.permissions?.includes("client.portal"))
  );
}

/**
 * Hududiy boshqarma xodimi — faqat o'z viloyati kabineti (`/hudud`). Boshqa ichki roli bor
 * xodim (masalan moderator) odatdagidek xodimlar kabinetiga tushadi.
 */
export function isRegionalUser(user: User | null | undefined): boolean {
  if (!user || user.scope_is_national) return false;
  const roles = user.role_codes ?? [];
  return roles.length > 0 && roles.every((code) => code === "regional_manager");
}

/** Kirishdan keyin ochiladigan bosh sahifa. */
export function homeRoute(user: User | null | undefined): string {
  if (isClientUser(user)) return "/mijoz";
  return isRegionalUser(user) ? "/hudud" : "/kabinet";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<Status>("checking");
  const [offline, setOffline] = useState(false);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then((profile) => {
        if (cancelled) return;
        setUser(profile);
        setStatus("authenticated");
        setRestored(true);
        setOffline(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setOffline(error instanceof ApiError && error.isOffline);
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Sessiya tugaganda (`sf:unauthorized` — refresh ham rad etilgan) foydalanuvchi
   * kirish sahifasiga qaytariladi: aks holda kabinet sahifalari «huquq yo'q»
   * xatolari bilan to'lib qoladi.
   */
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus("anonymous");
    };
    window.addEventListener("sf:unauthorized", onExpired);
    return () => window.removeEventListener("sf:unauthorized", onExpired);
  }, []);

  const setSession = useCallback((profile: User) => {
    setUser(profile);
    setRestored(false);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* sessiya allaqachon tugagan bo'lishi mumkin — baribir tozalaymiz */
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  /**
   * 15 daqiqalik faolsizlik sessiyasi. Faollik (sichqoncha, klaviatura, skroll)
   * `localStorage` ga yoziladi — bir nechta oynada bittasidagi harakat hammasini
   * «tirik» saqlaydi. Sahifa qayta ochilganda eski faollik vaqti tekshiriladi.
   */
  useEffect(() => {
    if (status !== "authenticated") return;
    let last = restored ? readActivity() || Date.now() : Date.now();
    const write = (now: number) => {
      last = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {
        /* xotira yopiq — faqat xotiradagi qiymat ishlaydi */
      }
    };
    if (!restored) write(Date.now());

    const touch = () => {
      const now = Date.now();
      if (now - last > 5000) write(now);
    };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, touch, { passive: true }));

    const timer = window.setInterval(() => {
      if (Date.now() - Math.max(last, readActivity()) <= IDLE_LIMIT_MS) return;
      try {
        sessionStorage.setItem(IDLE_FLAG_KEY, "1");
      } catch {
        /* ahamiyatsiz */
      }
      void signOut();
    }, 15_000);

    return () => {
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, touch));
      window.clearInterval(timer);
    };
  }, [status, restored, signOut]);

  const value = useMemo<AuthValue>(
    () => ({ user, status, offline, restored, setSession, signOut }),
    [user, status, offline, restored, setSession, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth faqat <AuthProvider> ichida ishlaydi");
  return ctx;
}
